import React, { useEffect, useMemo, useState } from 'react';
import useGameStore from '../store/gameStore';
import { toast } from '../components/ui/sonner';
import { Map, Globe2, TrendingUp, Landmark, Table2, Lock, Handshake } from 'lucide-react';
import { CarteReseau, FLAG_TYPE_COLORS, POS_BADGE } from '../components/developpement/CarteReseau';
import SyntheseReseau from '../components/developpement/SyntheseReseau';
import FranchisePanel from '../components/developpement/FranchisePanel';
import TunnelOuverture from '../components/ouverture/TunnelOuverture';
import {
  VILLES_FRANCE, SEUIL_INTERNATIONAL, getVille, NB_TYPES_MAGASIN,
  magasinsParVille, typesVille, nbMagasins, internationalDebloque, villesFranceDistinctes,
  statutVille, STATUTS_VILLE, bonusDominationPct, bonusReseauGeo,
} from '../utils/villes';
import { getTypeMagasin, getLocalisation, TYPES_MAGASIN_ACTIFS } from '../utils/ouverture';
import { getSecteur, getPositionnement } from '../utils/retail';
import { formatMoney } from '../utils/format';

const Legende = () => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-400" data-testid="carte-legende">
    <span className="font-bold uppercase tracking-wider text-slate-500">Formats :</span>
    {TYPES_MAGASIN_ACTIFS.map(t => (
      <span key={t.id} className="flex items-center gap-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full border border-black/30" style={{ background: FLAG_TYPE_COLORS[t.id] }} />
        {t.nom}
      </span>
    ))}
    <span className="ml-2 flex items-center gap-1"><span className="text-amber-400 font-bold">★</span> domination ({NB_TYPES_MAGASIN} formats)</span>
    <span className="flex items-center gap-1"><span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-amber-400 text-ink-900 text-[8px] font-black border border-dashed border-amber-700">F</span> franchisé</span>
    <span className="flex items-center gap-1"><span className="text-sm leading-none">🎭</span> événement local</span>
    <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-full bg-slate-300 border border-slate-500" /> ville libre</span>
  </div>
);

export default function Developpement() {
  const retail = useGameStore(s => s.retail);
  const company = useGameStore(s => s.company);
  const treasury = useGameStore(s => s.treasury || 0);
  const ouvrirMagasinTunnel = useGameStore(s => s.ouvrirMagasinTunnel);
  const totalDays = useGameStore(s => s.totalDays || 0);
  const [onglet, setOnglet] = useState('carte');
  const [villeChoisie, setVilleChoisie] = useState(null);
  const [tunnelOuvert, setTunnelOuvert] = useState(false);

  const parVille = useMemo(() => magasinsParVille(retail), [retail]);
  const nb = nbMagasins(retail);
  const nbVillesFr = villesFranceDistinctes(retail);
  const intlOk = internationalDebloque(retail);
  const geo = useMemo(() => bonusReseauGeo(retail), [retail]);
  const [vue, setVue] = useState(intlOk ? 'europe' : 'france');
  useEffect(() => { if (intlOk) setVue('europe'); }, [intlOk]);

  const ville = villeChoisie ? getVille(villeChoisie) : null;
  const magasinsVille = villeChoisie ? (parVille[villeChoisie] || []) : [];
  const typesPresents = villeChoisie ? typesVille(retail, villeChoisie) : [];
  const complet = typesPresents.length >= NB_TYPES_MAGASIN;
  const statut = STATUTS_VILLE[statutVille(typesPresents.length)];
  const secteurNom = getSecteur(retail?.secteur)?.nom || '';

  if (!retail?.magasin) {
    return <div className="p-6 text-sm text-slate-400">Ouvrez d'abord votre premier magasin pour accéder au développement du réseau.</div>;
  }

  const selectionner = (id) => { setVilleChoisie(id); setTunnelOuvert(false); };

  return (
    <div className="space-y-5" data-testid="page-developpement">
      <div className="flex flex-wrap items-center gap-3">
        <div className="p-2 rounded-lg bg-accent/15 text-accent"><Map className="w-5 h-5" /></div>
        <div>
          <h1 className="text-xl font-black text-foreground">Développement du réseau</h1>
          <p className="text-xs text-slate-400">Multipliez les formats ville après ville — jusqu'à la domination locale et les capitales voisines.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <div className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-1.5 text-center" data-testid="dev-nb-magasins">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Magasins</p>
            <p className="text-sm font-black text-foreground">{nb}</p>
          </div>
          <div className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-1.5 text-center" data-testid="dev-bonus-reseau">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">Bonus réseau</p>
            <p className={`text-sm font-black ${geo.totalPct > 0 ? 'text-emerald-400' : 'text-slate-400'}`}>+{geo.totalPct} %</p>
          </div>
          <div className="rounded-lg border border-ink-600 bg-ink-800/60 px-3 py-1.5 text-center" data-testid="dev-international-statut">
            <p className="text-[9px] uppercase tracking-wider text-slate-500">International</p>
            <p className={`text-sm font-black ${intlOk ? 'text-emerald-400' : 'text-amber-400'}`}>{intlOk ? 'Débloqué' : `${nbVillesFr}/${SEUIL_INTERNATIONAL} villes`}</p>
          </div>
        </div>
      </div>

      {/* Onglets Carte / Synthèse */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-lg bg-ink-900/60 border border-ink-600 p-0.5 w-max">
          <button type="button" onClick={() => setOnglet('carte')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${onglet === 'carte' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="dev-tab-carte">
            <Map className="w-3.5 h-3.5" /> Carte
          </button>
          <button type="button" onClick={() => setOnglet('synthese')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${onglet === 'synthese' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="dev-tab-synthese">
            <Table2 className="w-3.5 h-3.5" /> Synthèse réseau
          </button>
          <button type="button" onClick={() => setOnglet('franchise')}
            className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${onglet === 'franchise' ? 'bg-accent text-white' : 'text-slate-400 hover:text-slate-200'}`}
            data-testid="dev-tab-franchise">
            <Handshake className="w-3.5 h-3.5" /> Franchise
            {retail?.franchise?.candidature && <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />}
          </button>
        </div>
        {onglet === 'carte' && (
          <div className="flex gap-1 rounded-lg bg-ink-900/60 border border-ink-600 p-0.5 w-max">
            <button type="button" onClick={() => setVue('france')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${vue === 'france' ? 'bg-ink-700 text-foreground' : 'text-slate-400 hover:text-slate-200'}`}
              data-testid="dev-vue-france">
              <Landmark className="w-3.5 h-3.5" /> France
            </button>
            <button type="button" onClick={() => intlOk && setVue('europe')} disabled={!intlOk}
              title={intlOk ? '' : `Implantez-vous dans ${SEUIL_INTERNATIONAL} villes françaises pour débloquer l'Europe`}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors flex items-center gap-1.5 ${vue === 'europe' ? 'bg-ink-700 text-foreground' : intlOk ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 cursor-not-allowed'}`}
              data-testid="dev-vue-europe">
              {intlOk ? <Globe2 className="w-3.5 h-3.5" /> : <Lock className="w-3 h-3" />} Europe
            </button>
          </div>
        )}
      </div>

      {onglet === 'synthese' && <SyntheseReseau />}
      {onglet === 'franchise' && <FranchisePanel />}

      {onglet === 'carte' && (
        <>
          <Legende />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 surface-card bg-card border border-ink-600 rounded-xl p-4">
              <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2 flex items-center gap-1.5">
                {vue === 'france'
                  ? <><Landmark className="w-3.5 h-3.5" /> France — {VILLES_FRANCE.filter(v => parVille[v.id]?.length).length}/{VILLES_FRANCE.length} villes implantées</>
                  : <><Globe2 className="w-3.5 h-3.5" /> Europe — la France et les 7 capitales voisines</>}
              </p>
              <CarteReseau vue={vue} company={company} parVille={parVille} retail={retail}
                villeChoisie={villeChoisie} onSelect={selectionner} totalDays={totalDays} />
              {!intlOk && (
                <p className="mt-2 text-[10px] text-amber-300/90 flex items-center gap-1.5" data-testid="europe-verrouillee">
                  <Lock className="w-3 h-3" /> Encore {Math.max(0, SEUIL_INTERNATIONAL - nbVillesFr)} ville{SEUIL_INTERNATIONAL - nbVillesFr > 1 ? 's' : ''} française{SEUIL_INTERNATIONAL - nbVillesFr > 1 ? 's' : ''} à implanter : la carte s'ouvrira sur l'Europe et ses capitales.
                </p>
              )}
            </div>

            {/* Panneau ville */}
            <div className="lg:col-span-1 space-y-3">
              {!ville && (
                <div className="surface-card bg-card border border-ink-600 rounded-xl p-4 text-xs text-slate-400" data-testid="dev-panneau-vide">
                  <TrendingUp className="w-5 h-5 text-accent mb-2" />
                  Cliquez sur une ville pour voir son potentiel, vos implantations sur place et y ouvrir un nouveau format.
                  <p className="mt-3 text-[10px] text-slate-500">Un magasin de chaque format par ville ({NB_TYPES_MAGASIN} max). Tous les formats réunis = domination locale (+{bonusDominationPct(NB_TYPES_MAGASIN)} % de trafic sur place).</p>
                </div>
              )}
              {ville && (
                <div className="surface-card bg-card border border-accent/40 rounded-xl p-4 space-y-2" data-testid="dev-panneau-ville">
                  <div className="flex items-center gap-2">
                    <p className="text-base font-black text-foreground">{ville.drapeau} {ville.nom}</p>
                    {magasinsVille.length > 0 && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" data-testid="dev-ville-statut"
                        style={{ color: statut.couleur, borderColor: statut.couleur }}>
                        {statut.label}{complet ? ' ★' : ''}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-400">{ville.note}</p>
                  <div className="grid grid-cols-3 gap-1.5 text-center">
                    {[['Bassin', `×${ville.bassin.toFixed(2)}`, 'dev-ville-bassin'], ['Loyer', `×${ville.coef_loyer.toFixed(2)}`, 'dev-ville-loyer'], ['Travaux', `×${ville.coef_cout.toFixed(2)}`, 'dev-ville-cout']].map(([l, v, tid]) => (
                      <div key={tid} className="rounded-md bg-ink-800/70 border border-ink-600 py-1.5" data-testid={tid}>
                        <p className="text-[8px] uppercase tracking-wider text-slate-500">{l}</p>
                        <p className="text-xs font-bold font-mono text-foreground">{v}</p>
                      </div>
                    ))}
                  </div>
                  {magasinsVille.length > 0 && (
                    <div className="space-y-1" data-testid="dev-ville-magasins">
                      <p className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Vos implantations ({magasinsVille.length})</p>
                      {magasinsVille.map(({ magasin: m, principal }) => (
                        <div key={m.id || 'principal'} className="rounded-lg border border-ink-600 bg-ink-800/60 px-2 py-1.5 text-[11px]"
                          style={{ borderLeft: `3px solid ${FLAG_TYPE_COLORS[m.typeId] || '#94A3B8'}` }}>
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-foreground">{getTypeMagasin(m.typeId)?.nom || 'Magasin'}</span>
                            {secteurNom && <span className="text-slate-400">({secteurNom})</span>}
                            {principal && <span className="text-[8px] font-bold text-amber-400 border border-amber-400/50 rounded px-1">HQ</span>}
                            <span className="ml-auto text-slate-500 whitespace-nowrap">{formatMoney(m.loyerMensuel || 0)}/m</span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {getLocalisation(m.localisationId)?.nom || 'Emplacement historique'} · {POS_BADGE[m.positionnement] || '◆'} {getPositionnement(m.positionnement)?.nom || ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {complet ? (
                    <p className="text-[11px] text-amber-300 font-bold" data-testid="dev-ville-complete">
                      ★ Domination locale : les {NB_TYPES_MAGASIN} formats sont présents (+{bonusDominationPct(NB_TYPES_MAGASIN)} % de trafic sur place).
                    </p>
                  ) : ville.international && !intlOk ? (
                    <p className="text-[11px] text-amber-300" data-testid="dev-ville-verrouillee">🔒 Implantez-vous dans {SEUIL_INTERNATIONAL} villes françaises pour ouvrir à l'international.</p>
                  ) : (
                    <button onClick={() => setTunnelOuvert(true)}
                      className="w-full rounded-lg bg-accent hover:bg-accent-hover text-white text-xs font-bold py-2 transition-colors"
                      data-testid="dev-ouvrir-ici">
                      {magasinsVille.length > 0 ? `Ouvrir un autre format à ${ville.nom} (${typesPresents.length}/${NB_TYPES_MAGASIN})` : `Ouvrir un magasin à ${ville.nom}`}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tunnel d'ouverture pour la ville choisie */}
          {tunnelOuvert && ville && !complet && (
            <div className="surface-card bg-card border border-ink-600 rounded-xl p-4" data-testid="dev-tunnel-wrapper">
              <TunnelOuverture
                mode="expansion"
                secteurId={retail.secteur}
                secteurConfigs={retail.secteurConfigs || { [retail.secteur]: { positionnement: retail.positionnement, localisationId: retail.magasin?.localisationId || null } }}
                treasury={treasury}
                villeId={ville.id}
                typesExclus={typesPresents}
                onCancel={() => setTunnelOuvert(false)}
                onComplete={(cfg) => {
                  const res = ouvrirMagasinTunnel(cfg);
                  if (res?.ok) {
                    toast('Enseigne plantée !', { description: `${res.magasin.nom} ouvre à ${ville.nom} — ${formatMoney(res.cout)} ${cfg.financement === 'emprunt' ? 'financés par emprunt (48 mois)' : 'débités de la trésorerie'}.` });
                    setTunnelOuvert(false);
                  } else if (res?.reason === 'fonds') {
                    toast.error('Trésorerie insuffisante — choisissez le financement par emprunt.');
                  } else if (res?.reason === 'type_ville') {
                    toast.error('Ce format de magasin est déjà présent dans cette ville.');
                  }
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
